import React, { useMemo, useState } from 'react';

// components
import { FormButton } from '../ui/buttons/FormButton';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import {
  ModalContainer,
  CloseButtonContainer,
  ModalBody,
  ModalFooter,
  ModalFormContainer,
  ModalHeader,
  ModalHeaderInnerContainer,
  ModalHeaderMainText,
  ModalHeaderSecondaryText,
  ModalBoldText,
  ModalRegularText,
} from './modalComponents';
import ticketService from '../../ticketService';
import { PremiumInput } from '../ui/inputs/customInput';
import { Half, SectionContainer, SectionLine } from '@ontruck/fe-ui';
import t from './TicketUpdateTranslations';
import moment from 'moment';
import { ReactComponent as ImageIcon } from 'assets/icons/image.svg';
import AttachementLabel from '../ui/inputs/ui/AttachementLabel';
import AttachementItem from '../ui/inputs/ui/AttachementItem';
import commsLanguageDisplayText from 'modules/emergencies/constants/commsLanguageDisplayText';

export const TicketUpdateDelayed = ({ ticket, type, ok, cancel }) => {
  const commsLanguage = ticket.shipper_data?.language || 'es';
  const initialPickupCity = ticket.order_data?.pickup?.city || '';
  const initialPickupCountry = ticketService.utils.mapCountryCodeToCountryName(
    ticket.order_data?.pickup?.country || ''
  );
  const initialDropoffCity = ticket.order_data?.dropoff?.city || '';
  const initialDropoffCountry = ticketService.utils.mapCountryCodeToCountryName(
    ticket.order_data?.dropoff?.country || ''
  );
  const initialDate = moment().format('YYYY-MM-DD');

  const [pickupCity, setPickupCity] = useState(initialPickupCity);
  const [pickupCountry, setPickupCountry] = useState(initialPickupCountry);
  const [dropoffCity, setDroppoffCity] = useState(initialDropoffCity);
  const [dropoffCountry, setDropoffCountry] = useState(initialDropoffCountry);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState('');
  const [details, setDetails] = useState('');

  const [showErrors, setShowErrors] = useState(false);

  const [rawFiles, setRawFiles] = useState([]);

  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).filter((file) => {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif'];
        if (!allowedExtensions.includes(fileExtension)) {
          return false;
        }
        return true;
      });
      setRawFiles([...rawFiles, ...newFiles]);
    }
  };
  const removeFileByIndex = (index) => {
    setRawFiles(rawFiles.filter((_, i) => i !== index));
  };

  const formattedTicketId = useMemo(
    () => ticketService.utils.getFormatedId(ticket.id),
    [ticket.id]
  );

  const hasTimeError = useMemo(() => !time.match(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/), [time]);

  const hasDateError = useMemo(
    () => !date || !date.match(/(\d{4}\/\d{2}\/\d{2}|\d{4}-\d{2}-\d{2})/),
    [date]
  );

  const eta = useMemo(() => {
    if (hasTimeError || hasDateError) return null;
    return new Date(`${date} ${time}`);
  }, [date, time, hasTimeError, hasDateError]);

  const hasEtaError = useMemo(() => eta === null, [eta]);

  const hasPickupCityError = useMemo(() => pickupCity.trim().length === 0, [pickupCity]);

  const hasPickupCountryError = useMemo(() => pickupCountry.trim().length === 0, [pickupCountry]);

  const hasDropoffCityError = useMemo(() => dropoffCity.trim().length === 0, [dropoffCity]);

  const hasDropoffCountryError = useMemo(
    () => dropoffCountry.trim().length === 0,
    [dropoffCountry]
  );

  const hasDetailsError = useMemo(() => details.trim().length === 0, [details]);

  const hasErrors = useMemo(
    () =>
      hasEtaError ||
      hasDropoffCityError ||
      hasDropoffCountryError ||
      hasPickupCityError ||
      hasPickupCountryError ||
      hasDetailsError,
    [
      hasDetailsError,
      hasDropoffCityError,
      hasDropoffCountryError,
      hasEtaError,
      hasPickupCityError,
      hasPickupCountryError,
    ]
  );

  const goBack = (e) => {
    e.preventDefault();
    cancel();
  };

  const baseUpdateData = {
    load_detail: null,
    plate_number: null,
    //
    pickup_address: null,
    pickup_city: null,
    pickup_country: null,
    //
    dropoff_address: null,
    dropoff_city: null,
    dropoff_country: null,
    //
    eta: null,
    //
    additional_information: null,
  };

  const onSubmit = () => {
    setShowErrors(true);
    if (!hasErrors) {
      const eta_not_tz_aware = new Date(eta.getTime() - eta.getTimezoneOffset() * 60000);
      const dto = {
        update_data: {
          ...baseUpdateData,
          pickup_city: pickupCity,
          pickup_country: pickupCountry,
          dropoff_city: dropoffCity,
          dropoff_country: dropoffCountry,
          eta: eta_not_tz_aware,
          additional_information: details,
        },
        rawFiles,
        type,
      };
      ok(dto);
    }
  };

  return (
    <ModalContainer>
      <ModalBody>
        <ModalHeader>
          <ModalHeaderInnerContainer>
            <ModalHeaderSecondaryText>{`Ticket ID. ${formattedTicketId}`}</ModalHeaderSecondaryText>
            <ModalHeaderMainText>
              {t.sendMessage}: {t[type]}
            </ModalHeaderMainText>
          </ModalHeaderInnerContainer>
        </ModalHeader>
        <ModalFormContainer>
          {/* details */}
          <SectionContainer>
            <ModalBoldText>{t.incidentSection}</ModalBoldText>
            <SectionLine>
              <PremiumInput
                width={'100%'}
                type="text"
                label={t.detailsInputText}
                value={details}
                onChange={(e) => {
                  setDetails(e.target.value);
                }}
                isRequired={true}
                hasError={showErrors && hasDetailsError}
              />
            </SectionLine>
          </SectionContainer>
          {/* Pickup */}
          <SectionContainer>
            <ModalBoldText>{t.pickupSection}</ModalBoldText>
            <SectionLine>
              <Half>
                <PremiumInput
                  type="text"
                  label={t.city}
                  value={pickupCity}
                  onChange={(e) => setPickupCity(e.target.value)}
                  hasError={showErrors && hasPickupCityError}
                  isRequired={true}
                />
              </Half>
              <Half>
                <PremiumInput
                  type="text"
                  label={t.country}
                  value={pickupCountry}
                  onChange={(e) => setPickupCountry(e.target.value)}
                  hasError={showErrors && hasPickupCountryError}
                  isRequired={true}
                />
              </Half>
            </SectionLine>
          </SectionContainer>
          {/* Dropoff */}
          <SectionContainer>
            <ModalBoldText>{t.dropoffSection}</ModalBoldText>
            <SectionLine>
              <Half>
                <PremiumInput
                  type="text"
                  label={t.city}
                  value={dropoffCity}
                  onChange={(e) => setDroppoffCity(e.target.value)}
                  hasError={showErrors && hasDropoffCityError}
                  isRequired={true}
                />
              </Half>
              <Half>
                <PremiumInput
                  type="text"
                  label={t.country}
                  value={dropoffCountry}
                  onChange={(e) => setDropoffCountry(e.target.value)}
                  hasError={showErrors && hasDropoffCountryError}
                  isRequired={true}
                />
              </Half>
            </SectionLine>
          </SectionContainer>
          {/* eta */}
          <SectionContainer>
            <ModalBoldText>{t.etaSection}</ModalBoldText>
            <SectionLine>
              <Half>
                <PremiumInput
                  type="date"
                  label={t.dateInputText}
                  value={date}
                  onChange={(date) => setDate(date)}
                  hasError={showErrors && hasDateError}
                  isRequired={true}
                />
              </Half>
              <Half>
                <PremiumInput
                  label={t.timeInputText}
                  type="time"
                  onChange={setTime}
                  value={time}
                  hasError={showErrors && hasTimeError}
                  isRequired={true}
                />
              </Half>
            </SectionLine>
          </SectionContainer>

          {/* files */}
          <SectionContainer>
            <SectionLine>
              <AttachementLabel htmlFor="file">
                <input
                  id="file"
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif"
                  onChange={handleFileChange}
                  multiple
                  style={{ display: 'none' }}
                />
                <ImageIcon />
                Upload images
              </AttachementLabel>
            </SectionLine>
            <SectionLine>
              {rawFiles.map((file, index) => (
                <AttachementItem
                  key={index}
                  name={file.name}
                  onClick={() => removeFileByIndex(index)}
                />
              ))}
            </SectionLine>
          </SectionContainer>
          {/* comms lang */}
          <ModalRegularText>
            Language defined for the customer comms.: {commsLanguageDisplayText[commsLanguage]}
          </ModalRegularText>
        </ModalFormContainer>
      </ModalBody>
      <ModalFooter>
        <FormButton variant="ghost" onClick={goBack}>
          {t.back}
        </FormButton>
        <FormButton isDisabled={showErrors && hasErrors} onClick={onSubmit}>
          {t.send}
        </FormButton>
      </ModalFooter>
      <CloseButtonContainer onClick={goBack}>
        <CloseIcon />
      </CloseButtonContainer>
    </ModalContainer>
  );
};

export default TicketUpdateDelayed;
